import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../utils/api-client';

// Types

export interface FOSSyncStatus {
  status: string;
  last_sync_at?: string;
  last_sync_started_at?: string;
  decisions_found: number;
  decisions_imported: number;
  error_message?: string;
  processing_backlog: Record<string, number>;
  last_sync_config?: Record<string, unknown> | null;
  latest_synced_decision?: FOSLatestDecision | null;
}

export interface FOSSyncHealth {
  is_healthy: boolean;
  sync_lag_hours: number;
  last_sync_at?: string;
  last_sync_status: string;
  processing_rate: number;
  backlog_warning: boolean;
  backlog_count: number;
  error_count_24h: number;
  health_issues: string[];
}

export interface FOSCeleryTask {
  id: string;
  name: string;
  state: 'active' | 'reserved' | 'scheduled';
  worker: string;
  started_at?: string | null;
  eta?: string | null;
  args_repr?: string | null;
  kwargs_repr?: string | null;
}

export interface FOSCelerySummary {
  available: boolean;
  workers: number;
  active: number;
  reserved: number;
  scheduled: number;
  fos_active: number;
  fos_reserved: number;
  fos_scheduled: number;
  fos_tasks: FOSCeleryTask[];
  fos_tasks_by_name: Record<string, number>;
  generated_at: string;
  error?: string | null;
}

export interface FOSStats {
  total: number;
  by_status: Record<string, number>;
  by_outcome: Record<string, number>;
  by_processing_state: Record<string, number>;
  embedded_count: number;
}

export interface FOSBackfillStatus {
  total: number;
  pending: number;
  completed: number;
  failed: number;
  in_progress: number;
}

export interface FOSFailedDecision {
  id: string;
  reference: string;
  processing_status: string;
  business_name: string;
  industry_sector: string;
  decision_status: string;
  updated_at: string;
}

export interface FOSStuckDecision {
  id: string;
  reference: string | null;
  business_name: string | null;
  decision_date: string | null;
  industry_sector: string | null;
  processing_status: string;
  updated_at: string | null;
  last_task_id: string | null;
  last_task_name: string | null;
}

export interface FOSStuckDecisionsResponse {
  total: number;
  limit: number;
  offset: number;
  threshold_hours: number;
  decisions: FOSStuckDecision[];
}

export interface FOSFailedDecisionsResponse {
  total: number;
  limit: number;
  offset: number;
  decisions: FOSFailedDecision[];
}

export interface FOSSyncResponse {
  status: string;
  task_id: string;
  message: string;
}

export interface FOSSyncRequest {
  date_from?: string;
  date_to?: string;
  max_decisions?: number;
  industry_sector?: string;
}

export interface FOSLatestDecision {
  id: string;
  reference: string;
  decision_date: string | null;
  business_name: string | null;
  decision_status: string | null;
  industry_sector: string | null;
  imported_at: string | null;
}

export interface FOSReprocessRequest {
  status?: string;
  decision_ids?: string[];
}

export interface FOSReprocessResponse {
  status: string;
  task_id?: string;
  message: string;
  decisions_queued: number;
}

export interface FOSDecisionResumeResponse {
  status: string;
  action?: string | null;
  task_id?: string | null;
  message: string;
}

export interface FOSStuckRequeueRequest {
  decision_ids: string[];
}

export interface FOSStuckRequeueResponse {
  status: string;
  decisions_processed: number;
  decisions_queued: number;
  decisions_skipped: number;
}

export interface FOSCeleryRevokeRequest {
  task_ids: string[];
  terminate?: boolean;
}

export interface FOSCeleryRevokeResponse {
  status: string;
  revoked: number;
  message: string;
}

export interface FOSBackfillRequest {
  batch_size?: number;
}

export interface FOSBackfillResponse {
  status: string;
  task_id: string;
  message: string;
  batch_size?: number | null;
}

export interface FOSStopBackfillResponse {
  status: string;
  message: string;
}

export interface FOSDecision {
  id: string;
  reference: string;
  business_name: string | null;
  decision_date: string | null;
  decision_status: string;
  industry_sector: string;
  processing_status: string;
  summary: string | null;
  has_embedding: boolean;
  has_structured_summary: boolean;
  page_count: number | null;
  created_at: string;
  updated_at: string;
}

export interface FOSDecisionDetail {
  id: string;
  reference: string;
  business_name: string | null;
  decision_date: string | null;
  decision_status: string;
  industry_sector: string;
  processing_status: string;
  summary: string | null;
  structured_summary: Record<string, unknown> | null;
  redress: Record<string, unknown> | null;
  has_embedding: boolean;
  full_text_length: number;
  created_at: string;
  updated_at: string;
}

export interface FOSDecisionsFilters {
  processing_statuses: string[];
  decision_statuses: string[];
  industry_sectors: string[];
}

export interface FOSDecisionsResponse {
  total: number;
  limit: number;
  offset: number;
  decisions: FOSDecision[];
  filters: FOSDecisionsFilters;
}

export interface FOSDecisionQueryParams {
  processing_status?: string;
  decision_status?: string;
  industry_sector?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// Custom Hooks

/**
 * Hook to get FOS sync status
 * Auto-refreshes every 30 seconds
 */
export const useFOSSyncStatus = () => {
  return useQuery<FOSSyncStatus>({
    queryKey: ['admin', 'fos', 'sync', 'status'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/admin/fos/sync/status');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

/**
 * Hook to get FOS sync health indicators
 * Provides computed health metrics for dashboard display
 * Auto-refreshes every 60 seconds
 */
export const useFOSSyncHealth = () => {
  return useQuery<FOSSyncHealth>({
    queryKey: ['admin', 'fos', 'sync', 'health'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/admin/fos/sync/health');
      return response.data;
    },
    refetchInterval: 60000, // Refresh every 60 seconds
  });
};

/**
 * Hook to get Celery task summary for FOS pipeline
 */
export const useFOSCelerySummary = (
  options: { includeTasks?: boolean; limit?: number; taskPrefix?: string; timeout?: number } = {}
) => {
  return useQuery<FOSCelerySummary>({
    queryKey: ['admin', 'fos', 'celery', 'summary', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('include_tasks', String(options.includeTasks ?? true));
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.taskPrefix) params.append('task_prefix', options.taskPrefix);
      if (options.timeout) params.append('timeout', options.timeout.toString());
      const response = await apiClient.get(`/v1/admin/fos/celery/summary?${params.toString()}`);
      return response.data;
    },
    refetchInterval: 30000,
  });
};

/**
 * Hook to get FOS statistics
 */
export const useFOSStats = () => {
  return useQuery<FOSStats>({
    queryKey: ['admin', 'fos', 'stats'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/admin/fos/stats');
      return response.data;
    },
  });
};

/**
 * Hook to get FOS backfill status
 */
export const useFOSBackfillStatus = () => {
  return useQuery<FOSBackfillStatus>({
    queryKey: ['admin', 'fos', 'backfill', 'status'],
    queryFn: async () => {
      const response = await apiClient.get('/v1/admin/fos/backfill/status');
      return response.data;
    },
  });
};

/**
 * Hook to get failed FOS decisions with optional filtering and pagination
 */
export const useFOSFailedDecisions = (
  status?: string,
  limit: number = 50,
  offset: number = 0
) => {
  return useQuery<FOSFailedDecisionsResponse>({
    queryKey: ['admin', 'fos', 'decisions', 'failed', { status, limit, offset }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await apiClient.get(`/v1/admin/fos/decisions/failed?${params.toString()}`);
      return response.data;
    },
  });
};

/**
 * Hook to get stuck decisions
 */
export const useFOSStuckDecisions = (
  olderThanHours: number = 24,
  limit: number = 25,
  offset: number = 0,
  status?: string
) => {
  return useQuery<FOSStuckDecisionsResponse>({
    queryKey: ['admin', 'fos', 'decisions', 'stuck', { olderThanHours, limit, offset, status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('older_than_hours', olderThanHours.toString());
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      if (status) params.append('status', status);
      const response = await apiClient.get(`/v1/admin/fos/decisions/stuck?${params.toString()}`);
      return response.data;
    },
    refetchInterval: 60000,
  });
};

/**
 * Hook to trigger FOS sync
 */
export const useTriggerFOSSync = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSSyncResponse, Error, FOSSyncRequest | undefined>({
    mutationFn: async (request) => {
      const params = new URLSearchParams();
      if (request?.date_from) params.append('date_from', request.date_from);
      if (request?.date_to) params.append('date_to', request.date_to);
      if (request?.max_decisions) params.append('max_decisions', request.max_decisions.toString());
      if (request?.industry_sector) params.append('industry_sector', request.industry_sector);

      const queryString = params.toString();
      const response = await apiClient.post(
        queryString ? `/v1/admin/fos/sync?${queryString}` : '/v1/admin/fos/sync',
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'sync', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'overview'] });
    },
  });
};

/**
 * Hook to reprocess FOS decisions (bulk or by status)
 */
export const useReprocessDecisions = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSReprocessResponse, Error, FOSReprocessRequest>({
    mutationFn: async (request) => {
      const response = await apiClient.post('/v1/admin/fos/reprocess', request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'failed'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'overview'] });
    },
  });
};

/**
 * Hook to reprocess a single FOS decision by ID
 */
export const useReprocessSingleDecision = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSReprocessResponse, Error, string>({
    mutationFn: async (decisionId) => {
      const response = await apiClient.post(`/v1/admin/fos/decisions/${decisionId}/reprocess`, {});
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'failed'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'all'] });
    },
  });
};

/**
 * Hook to resume a single stuck decision
 */
export const useResumeFOSDecision = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSDecisionResumeResponse, Error, string>({
    mutationFn: async (decisionId) => {
      const response = await apiClient.post(`/v1/admin/fos/decisions/${decisionId}/resume`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'stuck'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'sync', 'status'] });
    },
  });
};

/**
 * Hook to requeue multiple stuck decisions
 */
export const useRequeueStuckDecisions = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSStuckRequeueResponse, Error, FOSStuckRequeueRequest>({
    mutationFn: async (request) => {
      const response = await apiClient.post('/v1/admin/fos/decisions/stuck/requeue', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'stuck'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'sync', 'status'] });
    },
  });
};

/**
 * Hook to revoke Celery tasks
 */
export const useRevokeCeleryTasks = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSCeleryRevokeResponse, Error, FOSCeleryRevokeRequest>({
    mutationFn: async (request) => {
      const response = await apiClient.post('/v1/admin/fos/celery/revoke', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'celery', 'summary'] });
    },
  });
};

/**
 * Hook to start FOS corpus backfill
 * This processes pending decisions through summarization and embedding
 */
export const useStartBackfill = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSBackfillResponse, Error, FOSBackfillRequest>({
    mutationFn: async (request = {}) => {
      const response = await apiClient.post('/v1/admin/fos/backfill', request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'overview'] });
    },
  });
};

/**
 * Hook to process pending embeddings
 * This queues embedding tasks for summarized decisions
 */
export const useProcessEmbeddings = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSReprocessResponse, Error, { limit?: number }>({
    mutationFn: async ({ limit } = {}) => {
      const query = limit ? `?limit=${limit}` : '';
      const response = await apiClient.post(`/v1/admin/fos/embed-pending${query}`, {});
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
    },
  });
};

/**
 * Hook to get all FOS decisions with filtering, search, and pagination
 */
export const useFOSDecisions = (params: FOSDecisionQueryParams = {}) => {
  return useQuery<FOSDecisionsResponse>({
    queryKey: ['admin', 'fos', 'decisions', 'all', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.processing_status) searchParams.append('processing_status', params.processing_status);
      if (params.decision_status) searchParams.append('decision_status', params.decision_status);
      if (params.industry_sector) searchParams.append('industry_sector', params.industry_sector);
      if (params.search) searchParams.append('search', params.search);
      if (params.sort_by) searchParams.append('sort_by', params.sort_by);
      if (params.sort_order) searchParams.append('sort_order', params.sort_order);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());

      const response = await apiClient.get(`/v1/admin/fos/decisions?${searchParams.toString()}`);
      return response.data;
    },
  });
};

/**
 * Hook to get a single FOS decision detail
 */
export const useFOSDecisionDetail = (decisionId: string | null) => {
  return useQuery<FOSDecisionDetail>({
    queryKey: ['admin', 'fos', 'decisions', 'detail', decisionId],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/admin/fos/decisions/${decisionId}`);
      return response.data;
    },
    enabled: !!decisionId,
  });
};

// -----------------------------------------------------------------------------
// Projection Management Types & Hooks
// -----------------------------------------------------------------------------

export interface FOSProjectionStatus {
  total_points: number;
  total_clusters: number;
  noise_count: number;
  computed_at: string | null;
  bounds: {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
  };
}

export interface FOSProjectionComputeRequest {
  n_neighbors?: number;
  min_dist?: number;
  min_cluster_size?: number;
}

export interface FOSProjectionComputeResponse {
  status: string;
  task_id: string;
  message: string;
  parameters: {
    n_neighbors: number;
    min_dist: number;
    min_cluster_size: number;
  };
}

export interface FOSProjectionRelabelResponse {
  status: string;
  task_id: string;
  message: string;
}

/**
 * Hook to get FOS projection status
 * Fetches from the public projections endpoint to get metadata
 */
export const useFOSProjectionStatus = () => {
  return useQuery<FOSProjectionStatus | null>({
    queryKey: ['admin', 'fos', 'projections', 'status'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/v1/fos/projections?include_clusters=false');
        const data = response.data;
        return {
          total_points: data.total_points,
          total_clusters: data.total_clusters,
          noise_count: data.noise_count,
          computed_at: data.computed_at,
          bounds: data.bounds,
        };
      } catch (error: unknown) {
        // Return null if projections not yet computed (404)
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    refetchInterval: 60000, // Refresh every 60 seconds
  });
};

/**
 * Hook to compute FOS projections
 */
export const useComputeProjections = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSProjectionComputeResponse, Error, FOSProjectionComputeRequest>({
    mutationFn: async (request = {}) => {
      const params = new URLSearchParams();
      if (request.n_neighbors !== undefined) params.append('n_neighbors', request.n_neighbors.toString());
      if (request.min_dist !== undefined) params.append('min_dist', request.min_dist.toString());
      if (request.min_cluster_size !== undefined) params.append('min_cluster_size', request.min_cluster_size.toString());

      const queryString = params.toString();
      const response = await apiClient.post(
        queryString ? `/v1/admin/fos/projections/compute?${queryString}` : '/v1/admin/fos/projections/compute',
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate projection status - will refetch when task completes
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'projections', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['fos', 'projections'] });
    },
  });
};

/**
 * Hook to regenerate cluster labels
 */
export const useRelabelClusters = () => {
  const queryClient = useQueryClient();

  return useMutation<FOSProjectionRelabelResponse, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post('/v1/admin/fos/projections/relabel', {});
      return response.data;
    },
    onSuccess: () => {
      // Invalidate projection queries to refetch cluster labels
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'projections', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['fos', 'projections'] });
    },
  });
};
